import { findVariant, mergeTheme, nocturne, type Theme } from '@ce/theme';
import type { AppConfig } from './config/types';
import { defaultAppConfig } from './config/defaults';
import { sanitizeConfig } from './config/resolve';
import type { ResolvedBundle, TenantBundle } from './types';
import { nocturneBundle } from './sample/nocturne';
import { defaultLevels } from './venue/layout';

/**
 * The CMS client.
 *
 * Contract: `GET {baseUrl}/tenants/{tenantId}/bundle` returns a TenantBundle.
 * Strategy is cache-first with background revalidation — the app must open
 * instantly on a convention-centre wifi network that may or may not exist, so a
 * cached bundle always wins the first paint and the network result is applied
 * when it lands.
 */

export interface Storage {
  getItem(key: string): Promise<string | null>;
  setItem(key: string, value: string): Promise<void>;
}

/** In-memory fallback so the client works with no storage wired up. */
export class MemoryStorage implements Storage {
  private map = new Map<string, string>();
  async getItem(key: string) { return this.map.get(key) ?? null; }
  async setItem(key: string, value: string) { this.map.set(key, value); }
}

export interface CmsConfig {
  baseUrl: string;
  tenantId: string;
  storage?: Storage;
  /** Bundled payload used when the network fails and nothing is cached. */
  fallback?: TenantBundle;
  fetchImpl?: typeof fetch;
}

const CACHE_PREFIX = 'ce.bundle.';

/**
 * Resolves the theme in three layers: Nocturne defaults, then the tenant's
 * brand from the CMS, then the attendee's chosen variant. The attendee can only
 * ever pick from what the client approved, so brand control survives.
 */
export function resolveBundle(
  bundle: TenantBundle,
  themeId: string | null = null,
  config?: AppConfig | null,
): ResolvedBundle {
  // Four layers, later winning: Nocturne defaults, the content API's own theme,
  // the admin's theme, then the attendee's chosen variant. The content-side
  // theme stays as a fallback so an existing tenant keeps its look until the
  // admin is used.
  const tenantTheme: Theme = mergeTheme(nocturne, bundle.theme);
  const base: Theme = config ? mergeTheme(tenantTheme, config.theme) : tenantTheme;
  const variants = config ? config.themeVariants : bundle.themeVariants;
  const variant = findVariant(variants, themeId);
  const theme: Theme = variant ? mergeTheme(base, variant.patch) : base;
  // Venue levels are optional in the payload; fall back to the generated plan.
  const venue = bundle.venue.levels.length
    ? bundle.venue
    : { ...bundle.venue, levels: defaultLevels() };
  // The picker reads variants off the bundle, so surface the admin's set there.
  const withVariants = config ? { ...bundle, venue, themeVariants: config.themeVariants } : { ...bundle, venue };
  return { bundle: withVariants, theme };
}

export class CmsClient {
  private cfg: CmsConfig;
  private storage: Storage;
  private doFetch: typeof fetch;

  constructor(cfg: CmsConfig) {
    this.cfg = cfg;
    this.storage = cfg.storage ?? new MemoryStorage();
    this.doFetch = cfg.fetchImpl ?? ((...a: Parameters<typeof fetch>) => fetch(...a));
  }

  private get cacheKey() {
    return `${CACHE_PREFIX}${this.cfg.tenantId}`;
  }

  private async readCache(): Promise<TenantBundle | null> {
    try {
      const raw = await this.storage.getItem(this.cacheKey);
      return raw ? (JSON.parse(raw) as TenantBundle) : null;
    } catch {
      return null;
    }
  }

  private async writeCache(bundle: TenantBundle) {
    try {
      await this.storage.setItem(this.cacheKey, JSON.stringify(bundle));
    } catch {
      // A full disk is not a reason to fail a launch.
    }
  }

  /** Network fetch. Throws on any non-2xx or transport error. */
  async fetchBundle(): Promise<TenantBundle> {
    const url = `${this.cfg.baseUrl.replace(/\/$/, '')}/tenants/${this.cfg.tenantId}/bundle`;
    const res = await this.doFetch(url, { headers: { accept: 'application/json' } });
    if (!res.ok) throw new Error(`CMS ${res.status} for ${url}`);
    return (await res.json()) as TenantBundle;
  }

  /**
   * Cache-first load. Returns immediately with whatever is available, then
   * calls `onUpdate` if the network returns a newer revision.
   */
  async load(onUpdate?: (b: ResolvedBundle) => void): Promise<ResolvedBundle> {
    const cached = await this.readCache();
    const fallback = this.cfg.fallback ?? nocturneBundle;
    const first = resolveBundle(cached ?? fallback);

    const revalidate = async () => {
      try {
        const fresh = await this.fetchBundle();
        if (fresh.revision !== (cached ?? fallback).revision) {
          await this.writeCache(fresh);
          onUpdate?.(resolveBundle(fresh));
        }
      } catch {
        // Offline is a normal state at a convention. Keep the cached bundle.
      }
    };

    if (cached) {
      void revalidate();
      return first;
    }

    // Nothing cached: try the network once before falling back, so a first
    // launch on a good connection gets real content rather than the sample.
    try {
      const fresh = await this.fetchBundle();
      await this.writeCache(fresh);
      return resolveBundle(fresh);
    } catch {
      return first;
    }
  }

  // ── Layout config ────────────────────────────────────────────────────────

  private get configKey() {
    return `ce.config.${this.cfg.tenantId}`;
  }

  /** Network fetch of the layout config. Throws on failure. */
  async fetchConfig(): Promise<unknown> {
    const url = `${this.cfg.baseUrl.replace(/\/$/, '')}/tenants/${this.cfg.tenantId}/config`;
    const res = await this.doFetch(url, { headers: { accept: 'application/json' } });
    if (!res.ok) throw new Error(`CMS ${res.status} for ${url}`);
    return res.json();
  }

  /**
   * Cache-first load of the layout config, same shape as `load`.
   *
   * The config is fetched and cached independently of content, so a content
   * outage never touches the layout and a layout publish never refetches the
   * programme. Whatever arrives is sanitised — the app never renders a config
   * it has not repaired — and a fetch that returns nothing usable falls back to
   * the default layout, which is the app as it was before the admin existed.
   */
  async loadConfig(onUpdate?: (c: AppConfig, notes: string[]) => void): Promise<AppConfig> {
    const tenant = this.cfg.tenantId;
    let cached: unknown = null;
    try {
      const raw = await this.storage.getItem(this.configKey);
      cached = raw ? JSON.parse(raw) : null;
    } catch { /* fall through to the default */ }

    const first = cached ? sanitizeConfig(cached, tenant).config : defaultAppConfig(tenant);

    const revalidate = async () => {
      try {
        const fresh = await this.fetchConfig();
        const clean = sanitizeConfig(fresh, tenant);
        if (clean.config.revision !== first.revision) {
          try { await this.storage.setItem(this.configKey, JSON.stringify(fresh)); } catch { /* full disk */ }
          onUpdate?.(clean.config, clean.notes);
        }
      } catch { /* offline is normal */ }
    };

    if (cached) {
      void revalidate();
      return first;
    }
    try {
      const fresh = await this.fetchConfig();
      const clean = sanitizeConfig(fresh, tenant);
      try { await this.storage.setItem(this.configKey, JSON.stringify(fresh)); } catch { /* full disk */ }
      return clean.config;
    } catch {
      return first;
    }
  }
}
